/**
 * Letter gap dialog (§1.6 Build 4c, D-124 ruling 3 — prompt-for-gaps, no placeholders ever).
 * When the per-record letter endpoint returns 422 with the required profile fields it can't
 * fill, this collects ONLY those, and hands them back to be saved to the executor profile +
 * used to generate the complete letter. No brackets ever reach the output.
 */
import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { serializeMultiSelect, parseMultiSelect, type MultiSelectOption } from "@shared/constants/form-field-tiers";
// s178 (A3): wording UNCHANGED — sourced from the shared module so the four decline strings
// have one home. The executor's form-gap sentence explains an empty FORM CELL, which is a
// different question from the planner-facing "you said you don't have this".
import { executorFormGapDeclineText, executorDeclineAction } from "@shared/constants/decline-copy";
import { type DeclineReason } from "@shared/constants/decline-reason";
import {
  parseAssignment, serializeAssignment, parseMerged, serializeMerged, recordCellKey,
  type AssignTarget, type DuplicateGroup,
} from "@shared/record-assignment";
import { SAGE, WARM } from "@shared/brand";


export interface LetterGapField {
  key: string;
  label: string;
  type: string;
  options?: string[];
  /** multi_select (Phase 6b) — stable key+label options rendered as checkboxes. */
  multiOptions?: MultiSelectOption[];
  /** record_select (s147) — the estate's OWN records (rowId + "Name (DOB)" label), a multi-select
   *  checklist; the answer is the serialized set of selected rowIds. Selecting NONE is valid. */
  recordOptions?: MultiSelectOption[];
  /** record_select (s148 CHILD-OVERFLOW-1) — the form's fixed row capacity + record noun; selecting more
   *  than `capacity` records shows a form-describing note that the rest go on an attached sheet. */
  capacity?: number;
  recordNoun?: string;
  /** record_select (s149) — which record list feeds this field; fields sharing a `recordsKey` are
   *  SIBLINGS (e.g. children_e1 / children_e3). One record lives in ONE sibling at a time (move-semantics
   *  in toggleMulti), matching the fill's one-record-per-section de-dupe. */
  recordsKey?: string;
  /** record_assign (s156 D pt.2(a)) — the mutually-exclusive grids this ONE control assigns to, in the
   *  form's print order. Each candidate (recordOptions) picks at most one target, so a person can never
   *  be placed in two grids: overlap is impossible by construction, not repaired at fill time. */
  assignTargets?: AssignTarget[];
  /** record_assign — suspected cross-section duplicates (the same name in two planner sections) for the
   *  executor to CONFIRM. Never merged silently (ruling 3). */
  duplicateGroups?: DuplicateGroup[];
  /** record_assign — the companion answer key carrying the confirmed-duplicate exclusions. */
  mergeKey?: string;
  /** record_assign — the assignment group's id; one half of every per-record cell answer key. Sent
   *  explicitly rather than derived from `key`, so the key format stays owned by one place (shared). */
  group?: string;
  /** record_assign (s156 D pt.2(c)) — the selects opened pre-filled with a STARTING SUGGESTION (the
   *  group has never been answered). Drives the "check these" framing and the per-row markers; false
   *  once the executor has saved, when the values shown are simply their own answers. */
  presortActive?: boolean;
  /** rowIds the pre-sort placed. */
  presorted?: string[];
  /** rowIds the plan couldn't place — these need the executor's decision. */
  unsorted?: string[];
  /** record_assign (s156 D pt.2(d)) — rowId → the `grid~column` fallback asks to SUPPRESS for that
   *  record, because the plan already holds the value. Planner-wins: never re-asked, never overridden. */
  plannerFilled?: Record<string, string[]>;
  /** The field's OWN starting values (s156 D pt.2(c)). The 422 first-generation path has no
   *  `initialValues` prop, so a gap that must open pre-filled carries its seed here. The prop still
   *  wins where both are present — a saved answer always beats a suggestion. */
  seed?: Record<string, string>;
  /** conditional field — offered but not required to submit (Phase 6b gov-form T2). */
  optional?: boolean;
  /** optional neutral helper copy under the field (Phase 6b gov-form T2 prompts). */
  note?: string;
  /** neutral capacity guidance for the form's blank, rendered under the input (Phase 6b DESIGN FIX). */
  capacityHint?: string;
  /** the form's blank is multi-line → render a textarea. */
  multiline?: boolean;
  /** s155 (FORM-GAP-DECLINE-CONTEXT-1, D-155.7): the PLANNER explicitly declined this field ("I don't
   *  have this"). The dialog surfaces "Planner indicated: not available" so a deliberate absence reads
   *  differently from a never-filled blank. Context only — the field stays skippable; never blocks. */
  declined?: boolean;
  /** 🔴 s179 (TWO-REASON-DECLINE-1): WHY the planner declined. Drives both the status sentence and
   *  the executor ACTION line beneath it. Absent → the conservative s178 wording, no action line. */
  declineReason?: DeclineReason;
  /** 🔴 s179 rebuild: the planner's location note. Leads the action line when present — their own
   *  words beat anything we generate. Rendered as text (React escapes it). */
  declineNote?: string;
}

// Reused verbatim by the gov-form gap flow (Phase 6b, s146) — only the copy is parameterized (heading /
// submitLabel / testId default to the letter wording). No forked component (G7).
export function LetterGapDialog({
  fields, onSubmit, onCancel, submitting,
  heading = "A few details for the letter (saved for next time):",
  submitLabel = "Generate letter",
  testId = "letter-gap-dialog",
  variant = "popover",
  initialValues = {},
  saved = false,
  error = null,
}: {
  fields: LetterGapField[];
  onSubmit: (values: Record<string, string>) => void;
  onCancel: () => void;
  submitting: boolean;
  heading?: string;
  submitLabel?: string;
  testId?: string;
  /** s147 (#7 route-back): seed the controls with the executor's CURRENT stored answers so re-opening
   *  the prompt shows their existing selection to change, not a blank slate. */
  initialValues?: Record<string, string>;
  /** "popover" (default) = inline absolute popover for the letter flow. "modal" = a fixed, centered,
   *  height-capped + internally-scrollable overlay (Phase 6b s146, BUG-1 fix): the gov-form gap dialog
   *  can be tall (74.1A = 5 T2 fields with long notes) and MUST NOT clip inside the task drawer's
   *  overflow-y-auto container — a fixed overlay escapes that clipping entirely. */
  variant?: "popover" | "modal";
  /** s156 (SAVE-DETERMINISM-1): the save has been CONFIRMED by the server. Shows an explicit "Saved"
   *  line and locks the controls for the moment before the dialog closes, so a closing dialog always
   *  means a save that actually landed — never the reverse. */
  saved?: boolean;
  /** s156: an error to show INSIDE the dialog — a save that could not be confirmed. The zone's inline
   *  error sits behind the modal overlay, so the modal carries its own. */
  error?: string | null;
}) {
  // Per-field seeds first, then the dialog-level initialValues — a SAVED answer always beats a
  // field's own starting suggestion where both exist.
  const [values, setValues] = useState<Record<string, string>>(() =>
    Object.assign({}, ...fields.map((f) => f.seed ?? {}), initialValues),
  );
  const set = (k: string, v: string) => setValues((prev) => ({ ...prev, [k]: v }));
  // Toggle one option of a multi_select field; the stored value is the serialized (sorted) selection.
  const toggleMulti = (k: string, optKey: string) => setValues((prev) => {
    const cur = new Set(parseMultiSelect(prev[k]));
    const adding = !cur.has(optKey);
    adding ? cur.add(optKey) : cur.delete(optKey);
    const next = { ...prev, [k]: serializeMultiSelect(Array.from(cur)) };
    // s149 one-section-per-child (move-semantics): selecting a record in a record_select field REMOVES it
    // from every SIBLING record_select (same recordsKey), so E1/E3 can never both hold the same child —
    // preventing the panel-vs-paper divergence the fill's de-dupe would otherwise silently resolve. This
    // is prevention, not age-gating: the executor still freely chooses which section (D-144.4).
    const field = fields.find((f) => f.key === k);
    if (adding && field?.type === "record_select" && field.recordsKey) {
      for (const sib of fields) {
        if (sib.key === k || sib.type !== "record_select" || sib.recordsKey !== field.recordsKey) continue;
        const sibSet = new Set(parseMultiSelect(next[sib.key]));
        if (sibSet.delete(optKey)) next[sib.key] = serializeMultiSelect(Array.from(sibSet));
      }
    }
    return next;
  });
  // record_assign (s156 D pt.2(a)) — ONE control assigning each candidate to at most ONE grid. Setting a
  // target REPLACES the previous one (it's a single choice per person), so the "same person in two grids"
  // state the three independent pickers allowed is simply not representable here.
  const setAssign = (k: string, rowId: string, gridKey: string) => setValues((prev) => {
    const a = parseAssignment(prev[k]);
    if (gridKey) a[rowId] = gridKey; else delete a[rowId]; // "" = not on this form
    return { ...prev, [k]: serializeAssignment(a) };
  });
  // Confirming a suspected cross-section duplicate EXCLUDES that record from the form (the person is
  // still listed once, via the record the executor kept) and clears any assignment it had. The executor
  // confirms — we only ever suggest (ruling 3: no silent auto-merge).
  const toggleMerged = (f: LetterGapField, rowId: string) => setValues((prev) => {
    if (!f.mergeKey) return prev;
    const set = new Set(parseMerged(prev[f.mergeKey]));
    const merging = !set.has(rowId);
    merging ? set.add(rowId) : set.delete(rowId);
    const next = { ...prev, [f.mergeKey]: serializeMerged(Array.from(set)) };
    if (merging) {
      const a = parseAssignment(prev[f.key]);
      delete a[rowId];
      next[f.key] = serializeAssignment(a);
    }
    return next;
  });

  // s147: explicit "leave blank" for OPTIONAL fields — a deliberate choice, not an absence. Distinct
  // from an escape-hatch OPTION ("Not Applicable" / "Other (specify):") which asserts a value on the
  // form; skipping leaves the cell BLANK. Choosing skip clears any typed value; the field then reads as
  // deliberately blank (control checked, input disabled). On submit it sends "" → the s146 skip record.
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const isSkipped = (k: string) => skipped.has(k);
  const toggleSkip = (k: string) => setSkipped((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k);
    else { next.add(k); setValues((pv) => ({ ...pv, [k]: "" })); }
    return next;
  });
  // Only REQUIRED fields must be filled to submit; optional fields (conditional dates, opt-ins, skips) ride along.
  const allFilled = fields.filter((f) => !f.optional).every((f) => (values[f.key] ?? "").trim() !== "");
  // Drives the modal width — an assignment row is far wider than a plain question (see the modal below).
  const hasAssignment = fields.some((f) => f.type === "record_assign");

  const fieldsContent = (
    <>
      <p className="text-[13px] mb-2" style={{ color: WARM.charcoal }}>
        {heading}
      </p>
      <div className="flex flex-col gap-2">
        {fields.map((f) => {
          const off = isSkipped(f.key); // deliberately-blank: dim + disable the input, highlight the skip
          const dim = off ? { opacity: 0.45, pointerEvents: "none" as const } : undefined;
          // multi_select (authored options) and record_select (the estate's OWN records, s147) both
          // render as a checkbox checklist; the answer is the serialized selected keys either way.
          const checkOpts = f.type === "multi_select" ? f.multiOptions : f.type === "record_select" ? f.recordOptions : undefined;
          return (
            <div key={f.key} className="block text-[12px]">
              <div className="font-medium" style={{ color: WARM.charcoal }}>{f.label}</div>
              {/* s155 (FORM-GAP-DECLINE-CONTEXT-1): the planner marked this "I don't have this" — surface it
                  as context so the executor sees a deliberate absence, not just an empty prompt. */}
              {f.declined && (
                <>
                  <span
                    className="block text-[12px] mt-0.5 italic"
                    style={{ color: WARM.taupe }}
                    data-testid={`letter-gap-declined-${f.key}`}
                  >
                    {executorFormGapDeclineText(f.declineReason)}
                  </span>
                  {/* 🔴 s179: the executor's NEXT ACTION, when there is one. `dont_have` returns null
                      — the fact does not exist, so telling the executor to do anything about it would
                      be inventing work. That null is the whole difference between the two classes. */}
                  {executorDeclineAction(f.declineReason, f.declineNote) && (
                    <span
                      className="block text-[12px] mt-0.5"
                      style={{ color: WARM.charcoal }}
                      data-testid={`letter-gap-decline-action-${f.key}`}
                    >
                      {executorDeclineAction(f.declineReason, f.declineNote)}
                    </span>
                  )}
                </>
              )}
              {f.note && <span className="block text-[12px] mt-0.5" style={{ color: WARM.charcoal }}>{f.note}</span>}
              <div style={dim}>
                {f.type === "record_assign" && f.assignTargets ? (
                  (() => {
                    const assignment = parseAssignment(values[f.key]);
                    const merged = new Set(parseMerged(values[f.mergeKey ?? ""]));
                    // Which records the duplicate check flagged, and what each was flagged against — so a
                    // row can carry its own "same person?" control right where the decision is made.
                    const dupPartners = new Map<string, string[]>();
                    for (const g of f.duplicateGroups ?? []) {
                      for (const m of g.members) {
                        dupPartners.set(m.rowId, g.members.filter((o) => o.rowId !== m.rowId).map((o) => o.label));
                      }
                    }
                    // s156 D pt.2(c) — the unsorted bucket, counted from what is CURRENTLY in the
                    // control (not the server's opening list), so it falls as the executor works and
                    // reads as progress rather than a stale figure.
                    const opts = f.recordOptions ?? [];
                    const stillUnsorted = f.presortActive
                      ? opts.filter((o) => !merged.has(o.key) && !assignment[o.key]).length
                      : 0;
                    const presortedIds = new Set(f.presorted ?? []);
                    return (
                      <div className="mt-1.5 flex flex-col gap-1.5" data-testid={`letter-gap-field-${f.key}`}>
                        {f.presortActive && (
                          <div
                            className="rounded px-2 py-1.5 text-[11px] font-medium"
                            style={{ backgroundColor: stillUnsorted ? "#FBF3E4" : "#EEF3EE", color: WARM.charcoal }}
                            data-testid={`letter-gap-unsorted-${f.key}`}
                          >
                            {stillUnsorted > 0
                              ? `${stillUnsorted} of ${opts.length} still need you to choose — they're marked below.`
                              : `All ${opts.length} have a place. Check each one before you save.`}
                          </div>
                        )}
                        {/* The form's OWN ¶ headings, once, as the legend — the executor decides against the
                            form's words, not a paraphrase of ours. */}
                        <div className="rounded px-2 py-1.5 text-[11px] leading-snug" style={{ backgroundColor: "#F5F1E8", color: WARM.charcoal }}>
                          {f.assignTargets.map((t) => (
                            <div key={t.gridKey} className="mb-0.5 last:mb-0">
                              <span className="font-semibold">{t.label}</span> — {t.heading}
                            </div>
                          ))}
                        </div>
                        {(f.recordOptions ?? []).map((o) => {
                          const isMerged = merged.has(o.key);
                          const partners = dupPartners.get(o.key);
                          const target = f.assignTargets!.find((t) => t.gridKey === assignment[o.key]);
                          // s156 D pt.2(b): a grid's typeable columns are asked ONLY once the person is
                          // actually assigned there — "who is this minor's guardian?" is meaningless
                          // until they're on ¶1, and asking it of everyone would bury the assignment.
                          // s156 D pt.2(d) — PLANNER WINS: a fallback ask is dropped for any record whose
                          // plan already holds that value, so a recorded fact is never re-asked. The
                          // executor's answer only ever fills a cell the plan left empty; supplying one
                          // does NOT re-sort the person (see the pre-sort note in record-assignment.ts).
                          const filled = new Set(f.plannerFilled?.[o.key] ?? []);
                          const cols = ((!isMerged && target?.executorColumns) || [])
                            .filter((c) => !(c.fallback && filled.has(`${target!.gridKey}~${c.key}`)));
                          return (
                            <div
                              key={o.key}
                              className="rounded px-2.5 py-2"
                              style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", opacity: isMerged ? 0.5 : 1 }}
                              data-testid={`letter-gap-assign-row-${o.key}`}
                            >
                              {/* Row line 1 — name · target · duplicate question. Sized so it reads on ONE
                                  line from laptop width up; below that it stacks rather than squeezing. */}
                              <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                                <span className="min-w-0 flex-1 text-[12px]" style={{ color: WARM.charcoal }}>
                                  {o.label}
                                  {/* s156 D pt.2(c) — mark what came from us versus what still needs the
                                      executor. Only while the suggestion is live; once they've saved, the
                                      values shown are their own and labelling them would be wrong. */}
                                  {f.presortActive && !isMerged && (
                                    assignment[o.key] ? (
                                      presortedIds.has(o.key) && (
                                        <span className="ml-1.5 rounded px-1 py-0.5 text-[10px] align-middle"
                                          style={{ backgroundColor: "#EEF3EE", color: SAGE[600] }}
                                          data-testid={`letter-gap-suggested-${o.key}`}>
                                          suggested — check
                                        </span>
                                      )
                                    ) : (
                                      <span className="ml-1.5 rounded px-1 py-0.5 text-[10px] align-middle"
                                        style={{ backgroundColor: "#FBF3E4", color: WARM.charcoal }}
                                        data-testid={`letter-gap-needs-decision-${o.key}`}>
                                        needs your decision
                                      </span>
                                    )
                                  )}
                                </span>
                                <select
                                  value={isMerged ? "" : (assignment[o.key] ?? "")}
                                  disabled={off || isMerged}
                                  onChange={(e) => setAssign(f.key, o.key, e.target.value)}
                                  data-testid={`letter-gap-assign-${f.key}-${o.key}`}
                                  // Wide enough for the LONGEST target label to read in full — a
                                  // truncated "Mentally-incapable adults who share in th…" leaves the
                                  // executor unsure which paragraph they just put someone on.
                                  className="w-full rounded-md px-2 py-1 text-[12px] outline-none sm:w-72 sm:shrink-0 lg:w-96"
                                  style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal }}
                                >
                                  <option value="">Not on this form</option>
                                  {f.assignTargets!.map((t) => (
                                    <option key={t.gridKey} value={t.gridKey}>{t.label}</option>
                                  ))}
                                </select>
                                {partners && (
                                  // Suspected cross-section duplicate: SURFACED for confirmation, never merged
                                  // for them. The match is by name alone, so the executor is the one who knows.
                                  <label
                                    className="flex items-start gap-1.5 text-[11px] sm:w-64 sm:shrink-0"
                                    style={{ color: WARM.taupe }}
                                    data-testid={`letter-gap-dup-${o.key}`}
                                  >
                                    <input type="checkbox" checked={isMerged} disabled={off} onChange={() => toggleMerged(f, o.key)} className="mt-0.5" />
                                    <span>
                                      Also listed as {partners.join(", ")} — tick if that is the same person, and
                                      this record won't be listed again.
                                    </span>
                                  </label>
                                )}
                              </div>
                              {/* Row line 2 — the cells this paragraph asks for that the plan has no answer
                                  to. Indented under the person they belong to, side by side at width. */}
                              {cols.length > 0 && (
                                <div
                                  className="mt-2 grid gap-2 border-t pt-2 sm:grid-cols-2"
                                  style={{ borderColor: WARM.stone }}
                                  data-testid={`letter-gap-cells-${o.key}`}
                                >
                                  {cols.map((c) => {
                                    const ck = recordCellKey(f.group ?? "", target!.gridKey, o.key, c.key);
                                    return (
                                      <label key={c.key} className={`block text-[11px] ${c.multiline ? "sm:col-span-2" : ""}`} style={{ color: WARM.charcoal }}>
                                        <span className="font-medium">{c.label}</span>
                                        {c.appendsTo && (
                                          <span className="block italic" style={{ color: WARM.taupe }} data-testid={`letter-gap-cell-appends-${o.key}-${c.key}`}>
                                            The form already prints what the plan holds for this cell — add anything further here; it appears on its own line.
                                          </span>
                                        )}
                                        {c.multiline ? (
                                          <textarea
                                            rows={2}
                                            value={values[ck] ?? ""}
                                            disabled={off}
                                            onChange={(e) => set(ck, e.target.value)}
                                            data-testid={`letter-gap-cell-${o.key}-${c.key}`}
                                            className="mt-0.5 w-full rounded-md px-2 py-1 text-[12px] outline-none resize-y"
                                            style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal }}
                                          />
                                        ) : (
                                          <input
                                            type="text"
                                            value={values[ck] ?? ""}
                                            disabled={off}
                                            onChange={(e) => set(ck, e.target.value)}
                                            data-testid={`letter-gap-cell-${o.key}-${c.key}`}
                                            className="mt-0.5 w-full rounded-md px-2 py-1 text-[12px] outline-none"
                                            style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal }}
                                          />
                                        )}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()
                ) : checkOpts ? (
                  <div className="mt-1 flex flex-col gap-1" data-testid={`letter-gap-field-${f.key}`}>
                    {checkOpts.map((o) => {
                      const checked = parseMultiSelect(values[f.key]).includes(o.key);
                      return (
                        <label key={o.key} className="flex items-start gap-1.5 text-[12px]" style={{ color: WARM.charcoal }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={off}
                            onChange={() => toggleMulti(f.key, o.key)}
                            data-testid={`letter-gap-opt-${f.key}-${o.key}`}
                            className="mt-0.5"
                          />
                          <span>{o.label}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : f.type === "select" && f.options ? (
                  <select
                    value={values[f.key] ?? ""}
                    disabled={off}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`letter-gap-field-${f.key}`}
                    className="mt-0.5 w-full rounded-md px-2 py-1.5 text-[13px] outline-none"
                    style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal }}
                  >
                    <option value="">Select…</option>
                    {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : f.multiline || f.key.toLowerCase().includes("address") ? (
                  // Multi-line: a mailing address (F-1 s134) OR a gov-form field whose blank spans multiple
                  // lines (Phase 6b DESIGN FIX) — render a textarea sized to the form's capacity.
                  <textarea
                    rows={3}
                    value={values[f.key] ?? ""}
                    disabled={off}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`letter-gap-field-${f.key}`}
                    className="mt-0.5 w-full rounded-md px-2 py-1.5 text-[13px] outline-none resize-y"
                    style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal }}
                  />
                ) : (
                  <input
                    type="text"
                    value={values[f.key] ?? ""}
                    disabled={off}
                    onChange={(e) => set(f.key, e.target.value)}
                    data-testid={`letter-gap-field-${f.key}`}
                    className="mt-0.5 w-full rounded-md px-2 py-1.5 text-[13px] outline-none"
                    style={{ border: `1px solid ${WARM.stone}`, backgroundColor: "#fff", color: WARM.charcoal }}
                  />
                )}
              </div>
              {/* s148 CHILD-OVERFLOW-1 — surfacing the form's fixed row capacity when the executor selects
                  more records than the form has slots. Describes the form, gives no advice; nothing is
                  dropped silently (the extra records go on the form's own attached-sheet provision). */}
              {f.type === "record_select" && (f.capacity ?? 0) > 0 && parseMultiSelect(values[f.key]).length > (f.capacity ?? 0) && (
                <div className="mt-1 text-[12px] font-medium" style={{ color: WARM.charcoal }} data-testid={`letter-gap-overflow-${f.key}`}>
                  This form has room for {f.capacity} {f.recordNoun ?? "records"} — the first {f.capacity} you select are filled in; the rest are added on a continuation sheet included with this download.
                </div>
              )}
              {f.capacityHint && (
                <span className="block text-[11px] mt-0.5" style={{ color: WARM.charcoal }} data-testid={`letter-gap-capacity-${f.key}`}>
                  {f.capacityHint}
                </span>
              )}
              {/* s147: distinct skip for OPTIONAL fields — rendered APART from the options; becomes visibly
                  chosen (sage, filled) so "I decided to leave this" reads differently from "not done yet". */}
              {f.optional && (
                <label
                  className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] rounded px-1.5 py-0.5 cursor-pointer"
                  style={{ color: off ? SAGE[600] : WARM.taupe, border: `1px dashed ${off ? SAGE[600] : WARM.stone}`, backgroundColor: off ? "#EEF3EE" : "transparent" }}
                  data-testid={`letter-gap-skip-${f.key}`}
                >
                  <input type="checkbox" checked={off} onChange={() => toggleSkip(f.key)} />
                  <span>Leave blank — I'll complete this on the form</span>
                </label>
              )}
            </div>
          );
        })}
      </div>
    </>
  );

  // s156: a failed/unconfirmed save must be visible WHERE THE EXECUTOR IS LOOKING. The zone's inline
  // error sits behind the modal overlay, so the modal carries its own copy.
  const errorContent = error ? (
    <div
      className="mb-2 rounded-md px-2.5 py-2 text-[12px]"
      style={{ backgroundColor: "#FDF2F2", border: "1px solid #E7B9B9", color: "#7A2E2E" }}
      role="alert"
      data-testid="letter-gap-error"
    >
      {error}
    </div>
  ) : null;

  const buttonsContent = saved ? (
    // Confirmed save — the executor sees this BEFORE the dialog closes (s156).
    <div className="flex items-center gap-1.5 text-[12px] font-semibold" style={{ color: SAGE[600] }} data-testid="letter-gap-saved">
      <Check className="h-3.5 w-3.5 inline-block align-[-2px]" aria-hidden="true" /> Saved
    </div>
  ) : (
    <>
      <button
        type="button"
        // Send EVERY shown field key (an untouched optional → explicit blank) so the server records a
        // skip for optionals the executor passed on — they were shown the question, so they aren't
        // re-nagged next time (s146 G4 fix). Required fields are gated by `allFilled` below.
        // record_assign also sends its companion merge key (the confirmed cross-section duplicates) and
        // every per-record cell the executor touched, so the assignment, the exclusions and the
        // guardian/value answers are persisted as ONE decision. A cleared cell rides along as an explicit
        // blank — clearing a value the executor no longer wants on the form has to actually clear it.
        onClick={() => onSubmit(Object.fromEntries(
          fields.flatMap((f) => [
            [f.key, values[f.key] ?? ""] as [string, string],
            ...(f.type === "record_assign" && f.mergeKey ? [[f.mergeKey, values[f.mergeKey] ?? ""] as [string, string]] : []),
            ...(f.type === "record_assign" && f.group
              ? Object.keys(values)
                  .filter((k) => k.startsWith(`${f.group}~`))
                  .map((k) => [k, values[k] ?? ""] as [string, string])
              : []),
          ]),
        ))}
        disabled={!allFilled || submitting}
        data-testid="letter-gap-submit"
        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1 text-[12px] font-semibold text-white disabled:opacity-50"
        style={{ backgroundColor: SAGE[600] }}
      >
        {submitting && <Loader2 className="h-3 w-3 animate-spin" />}
        {submitLabel}
      </button>
      <button type="button" onClick={onCancel} className="text-[12px]" style={{ color: WARM.taupe }} data-testid="letter-gap-cancel">
        Cancel
      </button>
    </>
  );

  // "modal" — a fixed, centered overlay that escapes the task-drawer's overflow-y-auto clipping. The
  // card is a flex column capped at 85vh: the fields SCROLL in the body while the action buttons live
  // in a STICKY footer, so the submit is ALWAYS visible no matter how tall the form (74.1A / empty
  // estate = 10 gap fields). This is the BUG-1 fix (s146) — a tall gap dialog is never unusable.
  if (variant === "modal") {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: "rgba(43,42,38,0.35)" }}
        data-testid={testId}
      >
        <div
          // s156 D pt.2(b): an assignment control carries a WIDE row — name + source, the target select,
          // the duplicate question, and (once assigned) the paragraph's guardian/value cells. At the old
          // max-w-xl those wrapped to four lines each and the list stopped scanning as a list. It gets a
          // wider cap; every other gap dialog keeps the narrow one (a column of short questions reads
          // worse stretched). Capped at 95vw so the laptop case never overflows the viewport.
          className={`w-full rounded-lg shadow-xl max-h-[85vh] flex flex-col ${
            hasAssignment ? "max-w-[95vw] sm:max-w-3xl lg:max-w-5xl xl:max-w-7xl" : "max-w-xs sm:max-w-xl"
          }`}
          style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }}
        >
          <div className="p-4 overflow-y-auto">{fieldsContent}</div>
          <div className="flex flex-col gap-2 p-3 border-t shrink-0" style={{ borderColor: WARM.stone }}>{errorContent}<div className="flex gap-2">{buttonsContent}</div></div>
        </div>
      </div>
    );
  }

  // "popover" (default) — the inline letter-flow popover, anchored to its RecordRow. Unchanged.
  return (
    <div
      className="absolute right-3 top-10 z-20 w-72 rounded-lg p-3 shadow-lg"
      style={{ backgroundColor: WARM.white, border: `1px solid ${WARM.stone}` }}
      data-testid={testId}
    >
      {fieldsContent}
      <div className="mt-3">{errorContent}</div>
      <div className="flex gap-2">{buttonsContent}</div>
    </div>
  );
}
